import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  addInventoryStock,
  adjustInventoryStock,
  removeInventoryStock,
  updateInventoryBarcodeNumber,
  updateInventorySku,
  updateInventoryUnit,
} from 'redux/actions/inventory/inventory';
import {
  selectCurrentBusiness,
  selectBusinessInventoryList,
} from 'redux/selectors';
import { checkDuplicateBarcodes } from 'utils/checkDuplicateBarcodes';
import { COUNT_TYPE } from 'components/MenuNew/FormView/Inventory/configs';
import { UPDATE_INVENTORY_TYPE } from 'components/MenuNew/hooks/useInventoryForm';

const useInventoryActions = (
  selectedInventory,
  open,
  onClose,
  uuid,
  inventoryFormMethods,
  currentType,
  productId
) => {
  const dispatch = useDispatch();
  const currentBusiness = useSelector(selectCurrentBusiness);
  const businessInventoryList = useSelector(selectBusinessInventoryList);

  const [valid, setIsValid] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [sku, setSku] = useState('');
  const [skuError, setSkuError] = useState('');

  const [unit, setUnit] = useState('');

  const [stock, setStock] = useState(0);
  const [stockError, setStockError] = useState('');

  const [barcodeNumber, setBarcodeNumber] = useState([]);
  const [originalBarcodeNumber, setOriginalBarcodeNumber] = useState([]);
  const [duplicateBarcodeModalOpen, setDuplicateBarcodeModalOpen] =
    useState(false);
  const [duplicateItems, setDuplicateItems] = useState([]);
  const [pendingBarcodePayload, setPendingBarcodePayload] = useState(null);

  // Reset state on dialog close
  useEffect(() => {
    if (!open) {
      setIsDirty(false);
      setIsValid(false);
      setBarcodeNumber([]);
      setOriginalBarcodeNumber([]);
      setSku('');
      setSkuError('');
      setUnit('');
      setStockError('');
      setStock(0);
      setDuplicateBarcodeModalOpen(false);
      setDuplicateItems([]);
      setPendingBarcodePayload(null);
    }
  }, [open]);

  // Populate initial values
  useEffect(() => {
    switch (selectedInventory?.type) {
      case UPDATE_INVENTORY_TYPE.SKU:
        setSku(selectedInventory?.data || '');
        break;
      case UPDATE_INVENTORY_TYPE.UNIT:
        setUnit(selectedInventory?.data || '');
        break;
      case UPDATE_INVENTORY_TYPE.BARCODE_NUMBER: {
        const initialBarcodes = selectedInventory?.data || [];
        setBarcodeNumber(initialBarcodes);
        setOriginalBarcodeNumber(initialBarcodes);
        break;
      }
      case UPDATE_INVENTORY_TYPE.STOCK:
        setStock(
          currentType === COUNT_TYPE.ADD ? 0 : selectedInventory?.data || 0
        );
        break;
      default:
        break;
    }
  }, [selectedInventory?.type, selectedInventory?.data]);

  // Generic save handler
  const handleSave = useCallback(
    async (action, payload, fieldKey, isBarcodeNumber = false) => {
      setIsLoading(true);
      try {
        await dispatch(action(payload));
        inventoryFormMethods.reset({
          ...inventoryFormMethods.getValues(),
          [fieldKey]: isBarcodeNumber ? payload['barcodes'] : payload[fieldKey],
        });
        if (isBarcodeNumber) {
          dispatch({
            type: 'SET_CURRENT_BARCODE_NUMBER',
            payload: payload['barcodes'],
          });
        }
        onClose();
      } catch (error) {
        console.error(`Failed to update ${fieldKey}:`, error);
      } finally {
        setIsLoading(false);
        setIsDirty(false);
        setIsValid(false);
      }
    },
    [onClose, inventoryFormMethods]
  );

  const actionMap = {
    [COUNT_TYPE.ADD]: (payload) => dispatch(addInventoryStock(payload)),
    [COUNT_TYPE.REMOVE]: (payload) => dispatch(removeInventoryStock(payload)),
    [COUNT_TYPE.ADJUST]: (payload) => dispatch(adjustInventoryStock(payload)),
  };

  const handleSaveStock = useCallback(
    async (type, payload) => {
      const action = actionMap[type];

      if (!action) {
        console.error(`No handler defined for type: ${type}`);
        return;
      }

      setIsLoading(true);

      try {
        action(payload); // call the handler function
        if (type === COUNT_TYPE.REMOVE) {
          payload.stock =
            Number(selectedInventory?.data) - Number(payload.stock);
        }

        if (type === COUNT_TYPE.ADD) {
          payload.stock =
            Number(selectedInventory?.data) + Number(payload.stock);
        }

        inventoryFormMethods.reset({
          ...inventoryFormMethods.getValues(),
          stock: payload.stock,
        });
        onClose(); // optional: close dialog after success
      } catch (error) {
        console.error(`Failed to perform stock action (${type}):`, error);
      } finally {
        setIsLoading(false);
        setIsDirty(false);
        setIsValid(false);
      }
    },
    [onClose, setIsLoading, setIsDirty, setIsValid]
  );

  const handleChangeStock = (e) => {
    const value = e.target.value.trim();

    setIsDirty(true);
    setStock(value);

    if (!value) {
      setIsValid(false);
      setStockError('Stock is required');
      return;
    }

    if (!/^\d+$/.test(value)) {
      setIsValid(false);
      setStockError('Stock must be a whole number');
      return;
    }

    setIsValid(true);
    setStockError('');
  };
  // Specific save handlers
  const handleUpdateStock = () => {
    handleSaveStock(currentType, {
      uuid,
      stock: stock,
      businessUuid: currentBusiness.uuid,
    });
  };

  // Perform actual barcode save
  const performBarcodeSave = useCallback(
    async (payload) => {
      setIsLoading(true);
      try {
        await dispatch(updateInventoryBarcodeNumber(payload));
        // Update original barcodes after successful save
        if (payload.barcodes) {
          setOriginalBarcodeNumber(payload.barcodes);
        }
        inventoryFormMethods.reset({
          ...inventoryFormMethods.getValues(),
          barcodeNumber: payload['barcodes'],
        });
        dispatch({
          type: 'SET_CURRENT_BARCODE_NUMBER',
          payload: payload['barcodes'],
        });
        onClose();
      } catch (error) {
        console.error('Failed to update barcodeNumber:', error);
      } finally {
        setIsLoading(false);
        setIsDirty(false);
        setIsValid(false);
      }
    },
    [onClose, inventoryFormMethods, dispatch]
  );

  // Specific save handlers
  const handleSaveBarcodeNumber = async () => {
    const payload = {
      uuid,
      barcodes: barcodeNumber,
      productId: productId,
    };

    // Check for duplicate barcodes using frontend data
    // Only check newly added barcodes, not already saved ones
    const normalizedCurrentBarcodes = (barcodeNumber || [])
      .map((b) => String(b).trim())
      .filter((b) => b);
    const normalizedOriginalBarcodes = (originalBarcodeNumber || [])
      .map((b) => String(b).trim())
      .filter((b) => b);

    // Find newly added barcodes (in current but not in original)
    const originalBarcodesSet = new Set(normalizedOriginalBarcodes);
    const newlyAddedBarcodes = normalizedCurrentBarcodes.filter(
      (barcode) => !originalBarcodesSet.has(barcode)
    );

    // Only check for duplicates on newly added barcodes
    if (newlyAddedBarcodes.length > 0) {
      const duplicateItemsData = checkDuplicateBarcodes(
        businessInventoryList || [],
        newlyAddedBarcodes,
        productId
      );

      if (duplicateItemsData.length > 0) {
        // Store the payload and show duplicate modal
        setPendingBarcodePayload(payload);
        setDuplicateItems(duplicateItemsData);
        setDuplicateBarcodeModalOpen(true);
        return;
      }
    }

    // No duplicates found or barcodes haven't changed, proceed with save
    await performBarcodeSave(payload);
  };

  const handleContinueWithDuplicate = async () => {
    setDuplicateBarcodeModalOpen(false);
    if (pendingBarcodePayload) {
      await performBarcodeSave(pendingBarcodePayload);
      setPendingBarcodePayload(null);
      setDuplicateItems([]);
    }
  };

  const handleCancelDuplicate = () => {
    setDuplicateBarcodeModalOpen(false);
    setPendingBarcodePayload(null);
    setDuplicateItems([]);
  };

  const handleSaveSku = () =>
    handleSave(updateInventorySku, { uuid, sku }, 'sku');

  const handleSaveUnit = () =>
    handleSave(updateInventoryUnit, { uuid, unit }, 'unit');

  const handleSelect = (e) => {
    setIsDirty(true);
    setIsValid(true);
    setUnit(e.target.value);
  };

  const handleChangeSku = (e) => {
    const value = e.target.value;
    setIsDirty(true);
    setSku(value);

    if (!value.trim()) {
      setIsValid(false);
      setSkuError('SKU is required');
    } else {
      setIsValid(true);
      setSkuError('');
    }
  };

  return {
    // Barcode
    setBarcodeNumber,
    handleSaveBarcodeNumber,
    duplicateBarcodeModalOpen,
    duplicateItems,
    handleContinueWithDuplicate,
    handleCancelDuplicate,

    // SKU
    sku,
    error: skuError,
    handleChangeSku,
    handleSaveSku,

    // Unit
    unit,
    handleSelect,
    handleSaveUnit,

    // Stock
    stock,
    handleChangeStock,
    handleUpdateStock,
    stockError,

    // General
    isDirty,
    valid,
    setIsDirty,
    setIsValid,
    isLoading,
  };
};

export default useInventoryActions;
